import {Button, Card, Col, Container, Row} from "react-bootstrap";
import {Link, useParams} from "react-router";
import {usePublishedProduct} from "./usePublishedProduct";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {formatCalendarDate} from "../../components/format";
import {ProductPrices} from "./ProductPrices";
import {RichText} from "../../components/RichText";
import {ProductGallery} from "./ProductGallery";
import {ClosedBetaNotice} from "../../components/ClosedBetaNotice";
import {htmlAfterLead, htmlDemoteHeadings, htmlSummary, htmlToText} from "../../components/sanitizeHtml";
import {productPath, softwareApplication} from "../../structuredData";
import {usePageMeta} from "../../components/usePageMeta";
import {accentProps} from "../../components/accent";
import {SellableEdition} from "../../api/models";
import {format, products, rich} from "../../i18n";
import {useSiteStatsHit} from "../../components/siteStats";
import {checkoutPath, useSale} from "./useSale";
import {ShowcaseActions, ShowcaseClosing, ShowcaseHero, ShowcaseListen} from "../showcase/Showcase";
import {Reveal} from "../showcase/Reveal";
import {TrademarkNotices, trademarksIn} from "../../components/TrademarkNotices";
import {ProductRequirementsSection, RequirementsSummary} from "./ProductRequirementsSection";

// A product's page, a showcase page (layout/showcasePages.ts), in the product's color: its hero, its demos, its images,
// then its description beside what it costs and what there is to download, its system requirements, and the closing
// panel.
export function ProductPage() {
    const {sku = ''} = useParams();
    const {data: product, error, loading} = usePublishedProduct(sku);
    const downloads = `/products/${encodeURIComponent(sku)}/download`;
    const {canBuy, paymentMethods} = useSale(product);
    useSiteStatsHit(product && { event: 'product-view', sku: product.sku });
    usePageMeta(product
        ? { title: product.name, description: htmlSummary(product.description), path: productPath(product.sku), image: product.images[0]?.url,
            jsonLd: softwareApplication(product, htmlToText(product.description)) }
        : {});

    const back = <Link to="/products" className="small"><i className="bi bi-arrow-left me-1"></i>{products.page.allProducts}</Link>;

    if (!product) {
        return (
            <Container className="py-4">
                <div className="mb-2">{back}</div>
                <ErrorAlert error={error} messages={{ 404: products.noSuchProduct }}/>
                {loading && <LoadingSpinner/>}
            </Container>
        );
    }

    return (
        <>
            <ShowcaseHero product={product} heading="h1" top={back}
                          actions={
                              <ShowcaseActions product={product} source="product-page" extra={product.currentVersion && (
                                  <Button as={Link as any} to={downloads} variant="showcase-outline" size="lg">
                                      <i className="bi bi-download me-2" aria-hidden="true"></i>{products.download}
                                  </Button>
                              )}/>
                          }/>
            <ShowcaseListen product={product}/>

            {product.images.length > 0 && (
                <section className="showcase-section" {...accentProps(product.accentColor)} aria-labelledby="product-gallery-title">
                    <Container>
                        <h2 id="product-gallery-title" className="showcase-title">{products.showcase.gallery}</h2>
                        {/* The hero shows the first image: start on the next. */}
                        <Reveal><ProductGallery images={product.images} start={1}/></Reveal>
                    </Container>
                </section>
            )}

            <section className="showcase-section" {...accentProps(product.accentColor)} aria-labelledby="product-about-title">
                <Container>
                    <h2 id="product-about-title" className="showcase-title">{format(products.showcase.about, { product: product.name })}</h2>
                    <Row className="gx-4 gy-5">
                        <Col lg={8} className="showcase-about">
                            {product.beta && (
                                <ClosedBetaNotice className="mb-4">
                                    {rich(products.page.betaNotice, { strong: text => <strong>{text}</strong> }, { product: product.name })}
                                </ClosedBetaNotice>
                            )}
                            {/* The hero already shows its first paragraph; its headings go under "About …". */}
                            <RichText html={htmlDemoteHeadings(htmlAfterLead(product.description))}/>
                            <TrademarkNotices marks={trademarksIn(product.description)} className="mt-4"/>
                        </Col>
                        <Col lg={4}>
                            <div className="showcase-aside">
                                <Card className="showcase-card mb-4">
                                    <Card.Body className="p-4">
                                        {product.prices.length > 0 && <div className="mb-3"><ProductPrices prices={product.prices}/></div>}
                                        {canBuy && (
                                            <div className="mb-3">
                                                <Button as={Link as any} to={checkoutPath(product)} variant="showcase" className="w-100">
                                                    <i className="bi bi-cart me-1" aria-hidden="true"></i>{format(products.page.buy, { product: product.name })}
                                                </Button>
                                                <p className="small text-body-secondary mt-2 mb-0">
                                                    {rich(products.page.buyTerms, { ticket: text => <Link to="/support/new">{text}</Link> },
                                                        { edition: SellableEdition, methods: paymentMethods })}
                                                </p>
                                            </div>
                                        )}
                                        {product.currentVersion ? (
                                            <>
                                                <p className="mb-2">
                                                    {rich(products.page.currentVersion, { strong: text => <strong>{text}</strong> },
                                                        { version: product.currentVersion.version, date: formatCalendarDate(product.currentVersion.releasedOn) })}
                                                </p>
                                                <p className="small text-body-secondary mb-0">
                                                    {rich(product.beta ? products.page.downloadsForBeta : products.page.downloadsForLicenseHolders, { link: text => <Link to={downloads}>{text}</Link> })}
                                                </p>
                                            </>
                                        ) : (
                                            <p className="text-body-secondary mb-0">{products.page.notAvailable}</p>
                                        )}
                                        <RequirementsSummary requirements={product.requirements}/>
                                    </Card.Body>
                                </Card>
                                {product.currentVersion?.releaseNotes && (
                                    <Card className="showcase-card">
                                        <Card.Body className="p-4">
                                            <h3 className="h6 mb-2">{format(products.page.whatsNew, { version: product.currentVersion.version })}</h3>
                                            <p className="product-text small text-body-secondary mb-0">{product.currentVersion.releaseNotes}</p>
                                        </Card.Body>
                                    </Card>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            <ProductRequirementsSection product={product}/>

            <ShowcaseClosing product={product} source="product-page"/>
            <div className="showcase-end"></div>
        </>
    );
}
